<template>
	<div :class="['default-card', color]" ref="defaultcard">
		<NuxtLinkLocale
			:to="link.href"
			aria-label="link.text"
			class="default-card__link"
		>
		</NuxtLinkLocale>

		<PictureImg
			v-if="poster && poster.value"
			class="default-card__poster picture--cover"
			:media="poster"
			loading="lazy"
		/>

		<div class="default-card__content">
			<div class="default-card__icon">
				<DefaultIcon
					v-if="logo && logo.src"
					class="default-card__icon-img"
					:logo="logo"
				>
				</DefaultIcon>
				<SvgIcon v-else-if="icon" class="default-card__icon-svg" :icon="icon" />
			</div>

			<h3 v-if="link && link.text" class="default-card__title">
				{{ link.text }}
			</h3>
			<div class="default-card__inner-content">
				<slot>
					<p v-if="content">{{ content }}</p>
				</slot>
			</div>
		</div>
	</div>
</template>

<script setup>
const props = defineProps({
	fields: { type: Object, required: true },
	rendering: { type: Object, required: false },
});

const content = computed(() => props.fields?.content?.value);
const poster = computed(() => props.fields?.poster?.value);
const link = computed(() => props.fields?.link?.value);
const icon = computed(() => props.fields?.icon?.value);
const logo = computed(() => props.fields?.logo?.value);
const color = computed(() => props.fields?.color?.value || "white");
</script>

<style lang="scss">
.default-card {
	position: relative;
	border-radius: 16px;
	padding: var(--spacing-2, 16px);
	border-radius: 8px;

	&:before {
		content: "";
		display: block;
		padding-top: 100%;
		float: left;
	}

	.default-card__content {
		font-size: var(--text-base, 16px);
		font-weight: 400;
		line-height: 140%;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-1, 8px);

		p {
			margin: 0;
		}
	}

	.default-card__icon-svg {
		width: 40px;
		height: 40px;
	}

	.default-card__icon .default-card__icon-img {
		height: 40px;
		justify-content: center;
		align-items: flex-start;
	}

	.default-card__icon .default-icon__img {
		height: 40px;
	}

	.default-card__title {
		font-size: var(--text-lg, 20px);
		font-weight: 700;
		line-height: 120%;
		margin: 0;
	}

	.default-card__poster {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: 1;

		img {
			height: 100%;
			width: 100%;
			object-fit: cover;
			object-position: center 15%;
		}
	}

	.default-card__link {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
	}

	.default-card__gradient {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: 2;
		background-image: linear-gradient(
			90deg,
			rgba(0, 0, 0, 0.9) 50%,
			transparent 70%
		);
	}

	&.orange,
	&.primary {
		color: var(--color-white, #fff);
		background-color: var(--primary-color, #fd470b);

		path {
			fill: var(--color-white, #fff);
		}
	}

	&.secondary {
		color: var(--color-black, #000);
		background-color: var(--secondary-color, #fd470b);

		path {
			fill: var(--color-black, #000);
		}
	}

	&.tertiary {
		color: var(--color-white, #fff);
		background-color: var(--tertiary-color, #384039);

		path {
			fill: var(--color-white, #fff);
		}
	}

	&.beige {
		color: var(--color-black, #000);
		background-color: #fffce2;

		path {
			fill: var(--color-black, #000);
		}
	}

	&.white {
		color: var(--color-black, #000);
		background-color: #fff;

		path {
			fill: var(--color-black, #000);
		}
	}

	&.glass {
		background: rgba(255, 255, 255, 0.2); // rgba(255, 252, 226, 0.10)
		box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
		backdrop-filter: blur(3px);
		-webkit-backdrop-filter: blur(5px);
		border: 1px solid rgba(255, 255, 255, 0.3);
		color: var(--color-white, #fff);
		path {
			fill: var(--color-white, #fff);
		}
	}

	&.glass-primary {
		background: rgb(
			from var(--primary-color, #fd470b) r g b / 0.5
		); // rgba(255, 252, 226, 0.10)
		box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
		backdrop-filter: blur(3px);
		-webkit-backdrop-filter: blur(5px);
		border: 1px solid rgba(255, 255, 255, 0.3);
		color: var(--color-white, #fff);

		path {
			fill: var(--color-white, #fff);
		}
	}

	&.glass-secondary {
		background: rgb(
			from var(--secondary-color, #fd470b) r g b / 0.5
		); // rgba(255, 252, 226, 0.10)
		box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
		backdrop-filter: blur(3px);
		-webkit-backdrop-filter: blur(5px);
		border: 1px solid rgba(255, 255, 255, 0.3);
		color: var(--color-white, #fff);

		path {
			fill: var(--color-white, #fff);
		}
	}

	&.glass-tertiary {
		background: rgb(
			from var(--tertiary-color, #fd470b) r g b / 0.5
		); // rgba(255, 252, 226, 0.10)
		box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
		backdrop-filter: blur(3px);
		-webkit-backdrop-filter: blur(5px);
		border: 1px solid rgba(255, 255, 255, 0.3);
		color: var(--color-white, #fff);

		path {
			fill: var(--color-white, #fff);
		}
	}

	&.glass-dark {
		background: rgba(0, 0, 0, 0.4);
		box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
		backdrop-filter: blur(3px);
		-webkit-backdrop-filter: blur(5px);
		border: 1px solid rgba(255, 255, 255, 0.3);
		color: var(--color-white, #fff);

		path {
			fill: var(--color-white, #fff);
		}
	}
}
</style>
